import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Avatar,
  LinearProgress,
  SvgIcon,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment
} from '@mui/material';
import { Pagination } from '@mui/material';
import {
  DirectionsBike,
  CheckCircle,
  PauseCircle,
  Schedule,
  ExitToApp,
  Star,
  Timeline,
  AccessTime,
  CardTravel,
  LocationOn,
  RateReview
} from '@mui/icons-material';
import { Moment } from 'moment';
import moment from 'moment';
import { DateRange } from '../../types/dateRange';

import Title from '../../components/Title';
import { TripDetailsModal } from '../../modals/TripDetailsModal';
import RefundModal from '../../modals/RefundModal';
import ReviewDescriptionModal from '../../modals/ReviewDescriptionModal';
import { formatDate } from '../../resources/functions';
import {
  Contract,
  TripTimeDialogState
} from '../../resources/types/contractsTypes';
import {
  exitContractAsync,
  getActiveContractsAsync,
  selectContracts
} from '../../redux/slices/contractsSlice';
import usePermissions from '../../hooks/usePermissions';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { selectAuth } from '../../redux/slices/authSlice';
import DateRangePicker from '../../components/DateRangePicker';
import { ReactComponent as CrownIcon } from '../../img/crown.svg';
import axios from '../../resources/axios';

const Root = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  gap: '20px'
}));

const CountersContainer = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 12,
  marginBottom: 20
});

const hexToRgba = (hex: string, alpha: number = 1): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

interface CounterCardProps {
  bgColor: string;
}

const CounterCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor'
})<CounterCardProps>(({ bgColor }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '14px 16px',
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: hexToRgba(bgColor, 0.12),
  border: 'none',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
  minWidth: 160,
  minHeight: 80,
  position: 'relative',
  
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  }
}));

const IconCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor'
})<CounterCardProps>(({ bgColor }) => ({
  width: 40,
  height: 40,
  borderRadius: '8px',
  backgroundColor: hexToRgba(bgColor, 0.18),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    width: 24,
    height: 24,
    color: bgColor
  }
}));

const CountText = styled(Box)<{ textColor: string }>(({ textColor }) => ({
  fontSize: 32,
  fontWeight: 700,
  color: textColor,
  lineHeight: 1,
  position: 'absolute',
  right: '16px',
  top: '50%',
  transform: 'translateY(-50%)'
}));

const LabelText = styled('span')(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
  textAlign: 'left'
}));

const FiltersCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  backgroundColor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#FFFFFF'
}));

const TripsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  flexGrow: 1,
  overflow: 'auto',
  paddingBottom: '16px'
});

const TripCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 8px 16px rgba(0,0,0,0.4)' 
      : '0 8px 16px rgba(0,0,0,0.1)'
  }
}));

const LIMIT = 10;

const getTripStatusConfig = (status?: string) => {
  const statusValue = status?.toLowerCase();
  
  switch (statusValue) {
    case 'normal':
      return {
        backgroundColor: 'rgba(244, 67, 54, 0.12)',
        iconColor: '#F44336',
        icon: DirectionsBike,
        tooltipKey: 'trips.scooterInTrip',
        labelKey: 'trips.inTrip'
      };
    case 'exit':
      return {
        backgroundColor: 'rgba(117, 117, 117, 0.12)',
        iconColor: '#757575',
        icon: CheckCircle,
        tooltipKey: 'trips.tripCompleted',
        labelKey: 'trips.completed'
      };
    case 'pause':
      return {
        backgroundColor: 'rgba(78, 205, 196, 0.12)',
        iconColor: '#4ECDC4',
        icon: PauseCircle,
        tooltipKey: 'trips.onPause',
        labelKey: 'trips.paused'
      };
    case 'reservation':
      return {
        backgroundColor: 'rgba(25, 118, 210, 0.12)',
        iconColor: '#1976D2',
        icon: Schedule,
        tooltipKey: 'trips.booking',
        labelKey: 'trips.booking'
      };
    default:
      return {
        backgroundColor: 'rgba(158, 158, 158, 0.12)',
        iconColor: '#9E9E9E',
        icon: DirectionsBike,
        tooltipKey: 'trips.unknownStatusTooltip',
        labelKey: 'trips.unknownStatus'
      };
  }
};

const TripsPage2 = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const checkPermission = usePermissions();

  const { activeContracts } = useSelector(selectContracts);
  const admins = useSelector(selectAdmins);
  const auth = useSelector(selectAuth);

  const isSuperAdmin = auth.info?.type === 'superAdmin';

  const [isActiveContracts, setIsActiveContracts] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'active' | 'paused' | 'reserved' | null>(null);
  const [tripDate, setTripDate] = useState<DateRange<Moment>>([null, null]);
  const [page, setPage] = useState(1);
  const [userPhone, setUserPhone] = useState('');
  const [scooterName, setScooterName] = useState('');
  const [tripDetailsDialogState, setTripDetailsDialogState] = useState<{
    open: boolean;
    tripId?: string;
    tripData?: any;
  }>({
    open: false
  });
  const [filterByHours, setFilterByHours] = useState<{
    from: Moment | null;
    to: Moment | null;
  } | null>(null);
  const [statistics, setStatistics] = useState({
    active: 0,
    paused: 0,
    reserved: 0
  });
  const [refundModalState, setRefundModalState] = useState<{
    open: boolean;
    rideId?: string;
    userId?: string;
    amount?: number;
    historyDetails?: any;
  }>({ open: false });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [reviewModalState, setReviewModalState] = useState<{
    open: boolean;
    rating?: number;
    description?: string;
  }>({ open: false });
  const [parkingAmountDialog, setParkingAmountDialog] = useState<{
    open: boolean;
    contractId?: string;
    value: string;
    current: number | null;
    saving: boolean;
  }>({ open: false, value: '', current: null, saving: false });

  // Helper function to build API params with filters
  const buildFilterParams = useCallback((additionalParams: any = {}) => {
    const cityId = admins.currentCity;
    const params: any = {
      limit: LIMIT,
      cityId,
      active: isActiveContracts,
      ...additionalParams
    };

    // Add status filter
    if (statusFilter === 'active') {
      params.active = true;
    } else if (statusFilter === 'paused') {
      params.status = 'pause';
    } else if (statusFilter === 'reserved') {
      params.status = 'reservation';
    }

    // Add text filters
    if (userPhone) {
      params.userPhone = userPhone;
    }
    if (scooterName) {
      params.scooterName = scooterName;
    }

    // Add date range filter if selected
    if (tripDate[0] && tripDate[1]) {
      let fromDate = tripDate[0].clone();
      let toDate = tripDate[1].clone();

      // Check if same day is selected
      const isSameDay = tripDate[0].toISOString() === tripDate[1].toISOString();

      if (isSameDay && (filterByHours?.from || filterByHours?.to)) {
        // Same day with hour filters - use specific hours
        if (filterByHours?.from) {
          fromDate = fromDate.clone()
            .hour(filterByHours.from.hour())
            .minute(filterByHours.from.minute());
        } else {
          fromDate = fromDate.startOf('day');
        }

        if (filterByHours?.to) {
          toDate = toDate.clone()
            .hour(filterByHours.to.hour())
            .minute(filterByHours.to.minute());
        } else {
          toDate = toDate.endOf('day');
        }
      } else {
        // Date range without hours - use full days
        fromDate = fromDate.startOf('day');
        toDate = toDate.endOf('day');
      }

      params.from = fromDate.valueOf();
      params.to = toDate.valueOf();
    }

    return params;
  }, [statusFilter, admins.currentCity, userPhone, scooterName, tripDate, filterByHours, isActiveContracts]);

  useEffect(() => {
    if (admins.currentCity) {
      const params = buildFilterParams({ page });
      dispatch(getActiveContractsAsync(params));
    }
  }, [dispatch, admins.currentCity, page, buildFilterParams]);

  // Fetch global statistics for counters
  useEffect(() => {
    const fetchStatistics = async () => {
      if (!admins.currentCity) return;

      try {
        // Build params to fetch ALL active contracts for counting
        const params: any = {
          limit: 1000, // Large limit to get all active contracts
          cityId: admins.currentCity,
          active: true
        };

        // Add text filters if present
        if (userPhone) {
          params.userPhone = userPhone;
        }
        if (scooterName) {
          params.scooterName = scooterName;
        }

        // Add date range filter if selected
        if (tripDate[0] && tripDate[1]) {
          let fromDate = tripDate[0].clone();
          let toDate = tripDate[1].clone();

          const isSameDay = tripDate[0].toISOString() === tripDate[1].toISOString();

          if (isSameDay && (filterByHours?.from || filterByHours?.to)) {
            if (filterByHours?.from) {
              fromDate = fromDate.clone()
                .hour(filterByHours.from.hour())
                .minute(filterByHours.from.minute());
            } else {
              fromDate = fromDate.startOf('day');
            }

            if (filterByHours?.to) {
              toDate = toDate.clone()
                .hour(filterByHours.to.hour())
                .minute(filterByHours.to.minute());
            } else {
              toDate = toDate.endOf('day');
            }
          } else {
            fromDate = fromDate.startOf('day');
            toDate = toDate.endOf('day');
          }

          params.from = fromDate.valueOf();
          params.to = toDate.valueOf();
        }

        console.log('📊 Fetching all active contracts for statistics:', params);

        // Fetch all active contracts
        const response = await axios.get('/contracts/active', { params });
        const contracts = response.data.contracts || [];

        console.log('📊 Total contracts fetched:', contracts.length);

        // Count statuses on frontend
        const activeCount = contracts.filter((c: any) => c.status?.value?.toLowerCase() === 'normal').length;
        const pausedCount = contracts.filter((c: any) => c.status?.value?.toLowerCase() === 'pause').length;
        const reservedCount = contracts.filter((c: any) => c.status?.value?.toLowerCase() === 'reservation').length;

        console.log('📊 Statistics (counted on frontend):');
        console.log('Active (normal):', activeCount);
        console.log('Paused:', pausedCount);
        console.log('Reserved:', reservedCount);

        setStatistics({
          active: activeCount,
          paused: pausedCount,
          reserved: reservedCount
        });
      } catch (error) {
        console.error('Failed to fetch statistics:', error);
        setStatistics({ active: 0, paused: 0, reserved: 0 });
      }
    };

    fetchStatistics();
  }, [admins.currentCity, userPhone, scooterName, tripDate, filterByHours]);

  const paginationCount = useMemo(
    () => Math.ceil(activeContracts.count / LIMIT),
    [activeContracts.count]
  );

  const onSubmitContractsFilters = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const formData = new FormData(event.currentTarget);
      const phone = formData.get('userPhone') as string;
      const scooter = formData.get('scooterName') as string;

      // Update state - this will trigger useEffect with new filters
      setUserPhone(phone);
      setScooterName(scooter);
      setPage(1);
    },
    []
  );

  const handleTripDate = (dates: DateRange<Moment>) => {
    setTripDate(dates);
  };

  const handleFilterByHoursFrom = (date: Moment | null) => {
    const newFilterByHours = { from: date, to: filterByHours?.to || null };
    setFilterByHours(newFilterByHours);
  };

  const handleFilterByHoursTo = (date: Moment | null) => {
    const newFilterByHours = { from: filterByHours?.from || null, to: date };
    setFilterByHours(newFilterByHours);
  };

  const onChangePage = useCallback(
    (event: React.ChangeEvent<unknown>, newPage: number) => {
      setPage(newPage);
    },
    []
  );

  const exitContract = useCallback(
    (contractId: string) => async () => {
      await dispatch(exitContractAsync(contractId));
      
      const params = buildFilterParams({ page });
      dispatch(getActiveContractsAsync(params));
    },
    [dispatch, page, buildFilterParams]
  );

  const handleTripDetailsClick = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement>,
      tripId: string,
      tripData: any
    ) => {
      event.stopPropagation();
      setTripDetailsDialogState({
        open: true,
        tripId,
        tripData
      });
    },
    []
  );

  const handleRefundClick = useCallback(
    (rideId: string, userId: string, amount: number, historyDetails?: any) => {
      setRefundModalState({
        open: true,
        rideId,
        userId,
        amount,
        historyDetails
      });
    },
    []
  );

  const handleRefundSuccess = useCallback(() => {
    setSnackbar({
      open: true,
      message: t('trips.refundSuccess'),
      severity: 'success'
    });

    const params = buildFilterParams({ page });
    dispatch(getActiveContractsAsync(params));
  }, [dispatch, page, buildFilterParams]);

  const openParkingAmountDialog = useCallback((contract: Contract) => {
    setParkingAmountDialog({
      open: true,
      contractId: contract._id,
      value:
        contract.parkingContractsStatsAmount != null
          ? String(contract.parkingContractsStatsAmount)
          : '',
      current: contract.parkingContractsStatsAmount ?? null,
      saving: false
    });
  }, []);

  const closeParkingAmountDialog = useCallback(() => {
    setParkingAmountDialog(prev => ({ ...prev, open: false }));
  }, []);

  const submitParkingAmount = useCallback(
    async (amount: number | null) => {
      const id = parkingAmountDialog.contractId;
      if (!id) return;

      setParkingAmountDialog(prev => ({ ...prev, saving: true }));
      try {
        await axios.put(`/contracts/${id}/parking_contracts_stats_amount`, {
          parkingContractsStatsAmount: amount
        });
        setSnackbar({
          open: true,
          message:
            amount === null
              ? t('trips.reportAmountReset')
              : t('trips.reportAmountSaved'),
          severity: 'success'
        });
        setParkingAmountDialog({
          open: false,
          value: '',
          current: null,
          saving: false
        });

        const params = buildFilterParams({ page });
        dispatch(getActiveContractsAsync(params));
      } catch (error) {
        setSnackbar({
          open: true,
          message: t('trips.reportAmountError'),
          severity: 'error'
        });
        setParkingAmountDialog(prev => ({ ...prev, saving: false }));
      }
    },
    [parkingAmountDialog.contractId, t, buildFilterParams, page, dispatch]
  );

  const handleSaveParkingAmount = useCallback(() => {
    const parsed = parseFloat(parkingAmountDialog.value.replace(',', '.'));
    if (!isFinite(parsed) || parsed < 0) {
      setSnackbar({
        open: true,
        message: t('trips.reportAmountInvalid'),
        severity: 'error'
      });
      return;
    }
    submitParkingAmount(parsed);
  }, [parkingAmountDialog.value, submitParkingAmount, t]);

  if (!checkPermission('contracts')) {
    return null;
  }

  return (
    <Root>
      <Title title={t('trips.title')} />

      <CountersContainer>
        <CounterCard 
          bgColor="#F44336"
          onClick={() => {
            setStatusFilter('active');
            setPage(1);
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <IconCircle bgColor="#F44336">
              <DirectionsBike />
            </IconCircle>
            <LabelText>{t('trips.inTrip')}</LabelText>
          </Box>
          <CountText textColor="#F44336">{statistics.active}</CountText>
        </CounterCard>

        <CounterCard 
          bgColor="#4ECDC4"
          onClick={() => {
            setStatusFilter('paused');
            setPage(1);
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <IconCircle bgColor="#4ECDC4">
              <PauseCircle />
            </IconCircle>
            <LabelText>{t('trips.paused')}</LabelText>
          </Box>
          <CountText textColor="#4ECDC4">{statistics.paused}</CountText>
        </CounterCard>

        <CounterCard 
          bgColor="#1976D2"
          onClick={() => {
            setStatusFilter('reserved');
            setPage(1);
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <IconCircle bgColor="#1976D2">
              <Schedule />
            </IconCircle>
            <LabelText>{t('trips.booking')}</LabelText>
          </Box>
          <CountText textColor="#1976D2">{statistics.reserved}</CountText>
        </CounterCard>
      </CountersContainer>

      {/* Filters */}
      <FiltersCard>
        <CardContent>
          <Box component="form" onSubmit={onSubmitContractsFilters}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item>
                    <TextField
                      label={t('trips.phone')}
                      name="userPhone"
                      defaultValue={userPhone}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '3px'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      label={t('trips.scooter')}
                      name="scooterName"
                      defaultValue={scooterName}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '3px'
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Grid container spacing={2} justifyContent="flex-end">
                  {(tripDate[0] || tripDate[1]) && tripDate[0]?.toISOString() === tripDate[1]?.toISOString() && (
                    <>
                      <Grid item>
                        <TextField
                          label={t('trips.timeFrom')}
                          type="time"
                          value={filterByHours?.from ? moment(filterByHours.from).format('HH:mm') : ''}
                          onChange={(e) => {
                            const timeValue = e.target.value;
                            if (timeValue) {
                              const [hours, minutes] = timeValue.split(':');
                              const newTime = moment().hour(parseInt(hours)).minute(parseInt(minutes));
                              handleFilterByHoursFrom(newTime);
                            }
                          }}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '3px'
                            }
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <TextField
                          label={t('trips.timeTo')}
                          type="time"
                          value={filterByHours?.to ? moment(filterByHours.to).format('HH:mm') : ''}
                          onChange={(e) => {
                            const timeValue = e.target.value;
                            if (timeValue) {
                              const [hours, minutes] = timeValue.split(':');
                              const newTime = moment().hour(parseInt(hours)).minute(parseInt(minutes));
                              handleFilterByHoursTo(newTime);
                            }
                          }}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '3px'
                            }
                          }}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item>
                    <Box
                      sx={{
                        '& .MuiTextField-root': {
                          '& .MuiOutlinedInput-root': {
                            height: '40px',
                            '& input': {
                              padding: '8.5px 14px'
                            }
                          }
                        }
                      }}
                    >
                      <DateRangePicker
                        startText={t('trips.from')}
                        endText={t('trips.to')}
                        value={tripDate}
                        onChange={handleTripDate}
                      />
                    </Box>
                  </Grid>

                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                    >
                      {t('trips.apply')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </FiltersCard>

      {/* Trips Cards */}
      <TripsContainer>
        {activeContracts.items.length === 0 ? (
          <Card sx={{ borderRadius: '12px', p: 4, textAlign: 'center' }}>
            <DirectionsBike sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              {t('trips.notFound')}
            </Typography>
          </Card>
        ) : (
          activeContracts.items.map(contract => {
            const statusConfig = getTripStatusConfig(contract.status?.value);
            const StatusIcon = statusConfig.icon;

            const tripDuration = contract.closedDate 
              ? moment.duration(moment(contract.closedDate).diff(moment(contract.createdAt))).asSeconds()
              : null;
            const formattedDuration = tripDuration 
              ? moment.utc(tripDuration * 1000).format('HH:mm:ss')
              : null;

            return (
              <TripCard key={contract._id}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    {/* Image on the left */}
                    <Grid item xs={12} sm={2} md={1}>
                      {contract.cableImg ? (
                        <a
                          href={contract.cableImg}
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          <img
                            alt="cableImg"
                            src={contract.cableImg}
                            style={{
                              width: '100%',
                              maxWidth: '80px',
                              height: 'auto',
                              borderRadius: '8px',
                              objectFit: 'cover'
                            }}
                          />
                        </a>
                      ) : (
                        <Box
                          sx={{
                            width: '80px',
                            height: '80px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '8px',
                            backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#E0E0E0',
                            color: 'textSecondary'
                          }}
                        >
                          <Typography variant="caption" align="center">
                            {t('trips.noPhoto')}
                          </Typography>
                        </Box>
                      )}
                    </Grid>

                    {/* Status Avatar and Trip Info */}
                    <Grid item xs={12} sm={10} md={3}>
                      <Box display="flex" gap={2} alignItems="center">
                        <Tooltip title={t(statusConfig.tooltipKey)} arrow>
                          <Avatar
                            sx={{
                              width: 48,
                              height: 48,
                              backgroundColor: statusConfig.backgroundColor,
                              borderRadius: '8px'
                            }}
                          >
                            <StatusIcon sx={{ fontSize: 28, color: statusConfig.iconColor }} />
                          </Avatar>
                        </Tooltip>
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Chip
                              size="small"
                              label={t(statusConfig.labelKey)}
                              sx={{
                                backgroundColor: statusConfig.backgroundColor,
                                color: statusConfig.iconColor,
                                fontWeight: 600,
                                borderRadius: '8px'
                              }}
                            />
                            {contract.active && (
                              <Chip
                                size="small"
                                label={t('trips.active')}
                                color="success"
                                sx={{ borderRadius: '8px' }}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            {t('trips.started')}: {formatDate(contract.createdAt, { time: true })}
                          </Typography>
                          {contract.closedDate && (
                            <Typography variant="body2" color="textSecondary">
                              {t('trips.finished')}: {formatDate(contract.closedDate, { time: true })}
                            </Typography>
                          )}
                          {formattedDuration && (
                            <Typography variant="body2" fontWeight="600">
                              {t('trips.duration')}: {formattedDuration}
                            </Typography>
                          )}
                          {contract.startCoords && (
                            <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                              <LocationOn sx={{ fontSize: 16, color: '#4CAF50' }} />
                              <Typography variant="body2" color="textSecondary">
                                <strong>{t('trips.startPoint')}:</strong> {contract.startCoords.lat.toFixed(6)}, {contract.startCoords.lon.toFixed(6)}
                              </Typography>
                            </Box>
                          )}
                          {contract.exitCoords && (
                            <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                              <LocationOn sx={{ fontSize: 16, color: '#F44336' }} />
                              <Typography variant="body2" color="textSecondary">
                                <strong>{t('trips.endPoint')}:</strong> {contract.exitCoords.lat.toFixed(6)}, {contract.exitCoords.lon.toFixed(6)}
                              </Typography>
                            </Box>
                          )}
                          {contract?.refund && (
                            <Typography variant="body2" sx={{ color: '#F44336', fontWeight: 600 }}>
                              {t('trips.refunded')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>

                    {/* Scooter Info */}
                    <Grid item xs={6} md={2}>
                      <Typography variant="subtitle2" color="textSecondary">
                        {t('trips.scooterCol')}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {contract.scooter?.name || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {t('trips.battery')}: {contract.scooter?.battery || 0}%
                      </Typography>
                    </Grid>

                    {/* User Info */}
                    <Grid item xs={6} md={2}>
                      <Typography variant="subtitle2" color="textSecondary">
                        {t('trips.userCol')}
                      </Typography>
                      {contract.user?.firstName && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {contract.user?.type === 'vip' && (
                            <SvgIcon
                              component={CrownIcon}
                              viewBox="0 0 1280 815"
                              sx={{ width: 16, height: 16 }}
                            />
                          )}
                          <Typography variant="body1" fontWeight="bold">
                            {contract.user.firstName}
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="body2" color="textSecondary">
                        {contract.user?.phone}
                      </Typography>
                    </Grid>

                    {/* Rating and Mileage */}
                    <Grid item xs={6} md={2}>
                      <Typography variant="subtitle2" color="textSecondary">
                        {t('trips.rating')}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                        {contract.contractReview?.rate ? (
                          <>
                            <Star sx={{ fontSize: 18, color: '#4ECDC4' }} />
                            <Typography variant="body1" fontWeight="bold">
                              {contract.contractReview.rate}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            {t('trips.none')}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        {t('trips.mileage')}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {contract.mileage || 0} {t('trips.km')}
                      </Typography>
                    </Grid>

                    {/* Action Buttons */}
                    <Grid item xs={6} md={2}>
                      <Box display="flex" flexDirection="column" gap={1}>
                        {checkPermission('history') && (
                          <Box display="flex" gap={0.5} justifyContent="flex-end">
                            <Tooltip title={t('trips.tripDetails')}>
                              <IconButton
                                size="medium"
                                onClick={event => handleTripDetailsClick(event, contract._id, contract)}
                                sx={{
                                  bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                                  color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                                  borderRadius: '8px',
                                  '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                                }}
                              >
                                <AccessTime fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}

                        {contract.contractReview?.description && (
                          <Button
                            size="small"
                            startIcon={<RateReview sx={{ fontSize: 16 }} />}
                            onClick={() => setReviewModalState({
                              open: true,
                              rating: contract.contractReview?.rate,
                              description: contract.contractReview?.description
                            })}
                            fullWidth
                            sx={{
                              fontSize: 12,
                              textTransform: 'none',
                              color: theme.palette.mode === 'light' ? '#5073F8' : '#90CAF9',
                              border: `1px solid ${theme.palette.mode === 'light' ? '#5073F8' : '#90CAF9'}`,
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'light' ? 'rgba(80, 115, 248, 0.08)' : 'rgba(144, 202, 249, 0.08)'
                              }
                            }}
                          >
                            {t('trips.textReview')}
                          </Button>
                        )}

                        {contract.active && (
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            fullWidth
                            onClick={exitContract(contract._id)}
                          >
                            {t('trips.finish')}
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </TripCard>
            );
          })
        )}
      </TripsContainer>

      {/* Pagination */}
      {paginationCount > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={paginationCount}
            page={page}
            onChange={onChangePage}
            sx={{
              '& .MuiPaginationItem-root': {
                color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                borderRadius: '8px'
              },
              '& .Mui-selected': {
                backgroundColor: theme.palette.mode === 'dark' ? '#FFFFFF !important' : '#000000 !important',
                color: theme.palette.mode === 'dark' ? '#000000 !important' : '#FFFFFF !important'
              }
            }}
          />
        </Box>
      )}

      {/* Modals */}
      <TripDetailsModal
        open={tripDetailsDialogState.open}
        tripId={tripDetailsDialogState.tripId}
        tripData={tripDetailsDialogState.tripData}
        onClose={() => setTripDetailsDialogState({ open: false })}
        onRefundClick={handleRefundClick}
        onEditReportAmount={isSuperAdmin ? openParkingAmountDialog : undefined}
      />

      <RefundModal
        open={refundModalState.open}
        rideId={refundModalState.rideId || ''}
        userId={refundModalState.userId || ''}
        amount={refundModalState.amount || 0}
        historyDetails={refundModalState.historyDetails}
        onClose={() => setRefundModalState({ open: false })}
        onSuccess={handleRefundSuccess}
      />

      <ReviewDescriptionModal
        open={reviewModalState.open}
        rating={reviewModalState.rating || 0}
        description={reviewModalState.description || ''}
        onClose={() => setReviewModalState({ open: false })}
      />

      <Dialog
        open={parkingAmountDialog.open}
        onClose={closeParkingAmountDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('trips.reportAmountTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {t('trips.reportAmountHint')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label={t('trips.reportAmountLabel')}
            value={parkingAmountDialog.value}
            onChange={e =>
              setParkingAmountDialog(prev => ({ ...prev, value: e.target.value }))
            }
            InputProps={{
              startAdornment: <InputAdornment position="start">€</InputAdornment>
            }}
            inputProps={{ min: 0, step: '0.01' }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button
            color="inherit"
            onClick={() => submitParkingAmount(null)}
            disabled={parkingAmountDialog.saving || parkingAmountDialog.current == null}
          >
            {t('trips.reportAmountResetBtn')}
          </Button>
          <Box display="flex" gap={1}>
            <Button onClick={closeParkingAmountDialog} disabled={parkingAmountDialog.saving}>
              {t('trips.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveParkingAmount}
              disabled={parkingAmountDialog.saving}
              sx={{
                backgroundColor: '#4ECDC4',
                '&:hover': { backgroundColor: '#3BA99F' }
              }}
            >
              {t('trips.save')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Root>
  );
};

export default TripsPage2;
